import type { CSSProperties, ReactNode } from 'react'
import { useState } from 'react'
import type { CompositionContextValue } from '@joyful/utils'
import { createNonNullableContext } from '@joyful/utils'
import type { CornerSlotFeature } from './cornerSlotPriority'
import { CornerSlotPriority } from './cornerSlotPriority'
export type Corner = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'

export interface CornerOffsets {
  bottom: number
  left: number
  right: number
  top: number
}

export interface CornerSlotRegistration {
  corner: Corner
  feature: CornerSlotFeature
  mountIndex: number
  width: number
}

// `mountIndex` is stamped by the store (used for stable tiebreaking in chain order).
export interface CornerSlotRegistrationInput {
  corner: Corner
  feature: CornerSlotFeature
  width: number
}

export type CornerSlotComputedStyle = Pick<
  CSSProperties,
  'bottom' | 'left' | 'right' | 'top' | 'zIndex'
>

export type CornerLayoutSnapshot = Readonly<
  Record<Corner, ReadonlyArray<CornerSlotRegistration>>
>

export interface CornerLayoutStore {
  getSnapshot: () => CornerLayoutSnapshot
  subscribe: (listener: () => void) => () => void
}

// Thin by design: slot data lives in the external store, subscribed via
// useSyncExternalStore so consumers only re-render when their own corner changes.
interface CornerLayoutState {
  store: CornerLayoutStore
}

interface CornerLayoutActions {
  register: (registration: CornerSlotRegistrationInput) => () => void
  updateWidth: (args: {
    corner: Corner
    feature: CornerSlotFeature
    width: number
  }) => void
}

interface CornerLayoutMeta {
  offsets: CornerOffsets
}

type CornerLayoutContextValue = CompositionContextValue<
  CornerLayoutState,
  CornerLayoutActions,
  CornerLayoutMeta
>

const DEFAULT_OFFSETS: CornerOffsets = {
  bottom: 16,
  left: 16,
  right: 16,
  top: 72,
}

const SLOT_GAP = 8
const BASE_Z_INDEX = 40

const CornerLayoutContext = createNonNullableContext<CornerLayoutContextValue>(
  'CornerLayoutContext',
)

export const useCornerLayoutContext = CornerLayoutContext.useRequiredContext

export interface CornerLayoutProviderProps {
  children: ReactNode
  // Distance in pixels from each viewport edge. Top defaults to 72 to clear the navbar.
  offsets?: Partial<CornerOffsets>
}

export function CornerLayoutProvider({
  children,
  offsets: offsetOverrides,
}: CornerLayoutProviderProps) {
  const [mutableStore] = useState<MutableCornerLayoutStore>(() => {
    return createCornerLayoutStore()
  })

  const offsets: CornerOffsets = {
    bottom: offsetOverrides?.bottom ?? DEFAULT_OFFSETS.bottom,
    left: offsetOverrides?.left ?? DEFAULT_OFFSETS.left,
    right: offsetOverrides?.right ?? DEFAULT_OFFSETS.right,
    top: offsetOverrides?.top ?? DEFAULT_OFFSETS.top,
  }

  return (
    <CornerLayoutContext.Context
      value={{
        actions: {
          register: mutableStore.register,
          updateWidth: mutableStore.updateWidth,
        },
        meta: { offsets },
        state: { store: mutableStore },
      }}
    >
      {children}
    </CornerLayoutContext.Context>
  )
}

// Internal write-enabled store shape; consumers see only the read surface (`CornerLayoutStore`).
interface MutableCornerLayoutStore extends CornerLayoutStore {
  register: CornerLayoutActions['register']
  updateWidth: CornerLayoutActions['updateWidth']
}

const EMPTY_SNAPSHOT: CornerLayoutSnapshot = {
  'bottom-left': [],
  'bottom-right': [],
  'top-left': [],
  'top-right': [],
}

function createCornerLayoutStore(): MutableCornerLayoutStore {
  let snapshot: CornerLayoutSnapshot = EMPTY_SNAPSHOT
  const listeners = new Set<() => void>()
  let mountCounter = 0
  let rafHandle: number | null = null

  const scheduleEmit = () => {
    if (rafHandle !== null) {
      return
    }

    rafHandle = requestAnimationFrame(() => {
      rafHandle = null
      for (const listener of listeners) {
        listener()
      }
    })
  }

  const replaceSnapshot = (
    mutator: (draft: Record<Corner, Array<CornerSlotRegistration>>) => void,
  ) => {
    const draft: Record<Corner, Array<CornerSlotRegistration>> = {
      'bottom-left': [...snapshot['bottom-left']],
      'bottom-right': [...snapshot['bottom-right']],
      'top-left': [...snapshot['top-left']],
      'top-right': [...snapshot['top-right']],
    }
    mutator(draft)
    snapshot = draft
    scheduleEmit()
  }

  return {
    getSnapshot: () => {
      return snapshot
    },

    register: (registration) => {
      const withMountIndex: CornerSlotRegistration = {
        ...registration,
        mountIndex: mountCounter++,
      }

      replaceSnapshot((draft) => {
        const list = draft[withMountIndex.corner]
        list.push(withMountIndex)
      })

      return () => {
        replaceSnapshot((draft) => {
          draft[withMountIndex.corner] = draft[withMountIndex.corner].filter(
            (entry) => {
              return entry.mountIndex !== withMountIndex.mountIndex
            },
          )
        })
      }
    },

    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },

    updateWidth: ({ corner, feature, width }) => {
      const list = snapshot[corner]
      const existing = list.find((entry) => {
        return entry.feature === feature
      })
      if (existing === undefined || existing.width === width) {
        return
      }

      replaceSnapshot((draft) => {
        draft[corner] = draft[corner].map((entry) => {
          return entry.feature === feature ? { ...entry, width } : entry
        })
      })
    },
  }
}

// Chain order: priority desc (closer to corner first), mount index asc as tiebreak.
export function sortSlotsForCorner(
  slots: ReadonlyArray<CornerSlotRegistration>,
) {
  return [...slots].sort((a, b) => {
    const priorityA = CornerSlotPriority[a.feature]
    const priorityB = CornerSlotPriority[b.feature]
    if (priorityA !== priorityB) {
      return priorityB - priorityA
    }

    return a.mountIndex - b.mountIndex
  })
}

// chainRank: 0 = closest to the corner. priorSlotsWidth: sum of widths of slots closer than this one.
export function computeSlotStyle(args: {
  chainLength: number
  chainRank: number
  corner: Corner
  offsets: CornerOffsets
  priorSlotsWidth: number
}): CornerSlotComputedStyle {
  const { chainLength, chainRank, corner, offsets, priorSlotsWidth } = args

  const gapContribution = chainRank === 0 ? 0 : chainRank * SLOT_GAP
  const horizontalOffset = priorSlotsWidth + gapContribution

  const style: CornerSlotComputedStyle = {
    zIndex: BASE_Z_INDEX + (chainLength - chainRank - 1),
  }

  if (corner === 'bottom-left' || corner === 'top-left') {
    style.left = offsets.left + horizontalOffset
  } else {
    style.right = offsets.right + horizontalOffset
  }

  if (corner === 'bottom-left' || corner === 'bottom-right') {
    style.bottom = offsets.bottom
  } else {
    style.top = offsets.top
  }

  return style
}
